/* eslint-disable no-cond-assign */
/**
 * @module msysCrypt
 * @description
 * msysCrypt library provides cryptography related functions.
 * It uses either crypto built-in library or alternatively node-forge third party library.
 * Cryptographic functions could take some time and block other main process functions,
 * which is especially problematic for mServer handling many parallel requests from various clients.
 * Therefore for mServer a special crypto worker is created on application
 * start using setupBackendWorker function to offload the processing to separate worker.
 * One of key functions is oid, which is used throughout Mosy components
 * to generate unique ID within the application.
 * It actually encodes microseconds into a string with radix 62 (letters and numbers only)
 * with padding returning always 5 glyphs. This is e.g. used for generating new
 * application ID by server (where generated 5 glyphs are prefixed with a letter indicating type of application).
 * Library provides various methods for crypto functionality:
 * - hashes: md5, generateHash, validateHash, challenge, hash32, hash10
 * - keys: generateKeyPair, verifyKeyPair
 * - certificates: createCertificate, createCertificateSync, verifyCertificateWithMyCA, verifyMyCertificate
 * - sign/verify electronic signature: sign, verifySignature
 * - conversions: privateFromPEM, privateToPEM, decryptPEM, publicFromPEM, publicToPEM
 *
 * Example of use:
 * const mcrypt = require('msysCrypt')
 * mcrypt.challange()
 *
 * Some openssl examples:
 * Sign:
 * openssl dgst -sha256 -sign certs/mRadar.SD2D0001.key test.js | openssl base64 > test.sig
 * Verify signature:
 * openssl dgst -sha256 -verify  <(openssl x509 -in certs/mRadar.SD2D0001.cer -pubkey -noout) -signature <(openssl base64 -d -in test.sig) test.js
 */
const mcrypt = module.exports = {}
const fs = require('fs')
const crypto = require('crypto')
const cluster = require('cluster')
const child = require('child_process')
const os = require('os')
const forge = require('node-forge')
const xxhash = require('xxhashjs')

const KEY_SIZE = 2048
mcrypt.RADIX62 = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'
mcrypt.RADIX64 = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz+/'

let cryptoWorker
const requests_generateKeyPair = []
const requests_createCertificate = []
const requests_verifyKeyPair = []

const OPENSSL_COMMAND_GENERATE_KEY = '/usr/bin/openssl genrsa 2048'
const OPENSSL_COMMAND_EXPORT_PUBLIC_KEY = '/usr/bin/openssl rsa -outform PEM -pubout'

mcrypt.setupBackendWorker = function() {
  if(!cluster) return false
  if(cluster.isMaster) {
    // MASTER
    cryptoWorker = cluster.fork()

    // Handle exit/crash of cryptoWorker
    cluster.on('exit', (worker, code, signal) => {
      if(mcrypt.system_shutdown) return
      cryptoWorker = undefined

      // SERIOUS Problem ->the cryptoWorkerhad died -> we restart it
      console.error('cryptoWorker %d died (%s). restarting..., Rescheduling pending cryptoWork', worker.process.pid, signal || code)

      // We need to empty the requests - we will resubmit queries callback version but without cryptoWorker (is set to undefined)
      // Handle -> generateKeyPair
      let request
      while(request = requests_generateKeyPair.shift()) {
        const { key_password, callback } = request
        mcrypt.generateKeyPair(key_password, callback)
      }

      // Handle -> createCertificate
      while(request = requests_createCertificate.shift()) {
        const { ca_cert_pem, ca_key_pem, ca_key_pass, cert_valid_seconds, app_id, apptype, app_pub_pem, callback } = request
        mcrypt.createCertificate(ca_cert_pem, ca_key_pem, ca_key_pass, cert_valid_seconds, app_id, apptype, app_pub_pem, callback)
      }

      // Handle -> verifyKeyPair
      while(request = requests_verifyKeyPair.shift()) {
        const { public_key, private_key, private_pass, callback } = request
        mcrypt.verifyKeyPair(public_key, private_key, private_pass, callback)
      }

      cryptoWorker = cluster.fork()
    })

    // Handle response from cryptoWorker. Find proper callback and call it
    cluster.on('message', (worker, { action, err, response_data }) => {
      if(action === 'generateKeyPair') {
        const request = requests_generateKeyPair.shift()
        const { keypair, public_pem, private_pem } = response_data
        const { callback } = request
        if(err || !keypair) return callback()
        callback([public_pem, private_pem])
      }else if(action === 'createCertificate') {
        const request = requests_createCertificate.shift()
        const { certificate } = response_data
        const { callback } = request
        callback(null, certificate)
      }else if(action === 'verifyKeyPair') {
        const request = requests_verifyKeyPair.shift()
        const { verify_result } = response_data
        const { callback } = request
        callback(null, verify_result)
      }
    })

    return true
  }else{
    // WORKER
    // Short hand notation so we can access the worker object in the same way from Master and Worker thread
    cryptoWorker = cluster.worker
    cryptoWorker.on('message', ({ action, data = {} }) => {
      if(action === 'generateKeyPair') {
        const key_password = data.key_password
        mcrypt._generateKeyPair(key_password, (keypair) => {
          const result = { action: 'generateKeyPair', response_data: { keypair } }
          if(keypair && key_password) {
            result.response_data.public_pem = keypair[0]
            result.response_data.private_pem = keypair[1]
          }
          process.send(result)
        })
      }else if(action === 'createCertificate') {
        const { ca_cert_pem, ca_key_pem, ca_key_pass, cert_valid_seconds, app_id, apptype, app_pub_pem } = data
        const certificate = mcrypt._createCertificate(ca_cert_pem, ca_key_pem, ca_key_pass, cert_valid_seconds, app_id, apptype, app_pub_pem)
        const result = { action: 'createCertificate', response_data: { certificate } }
        process.send(result)
      }else if(action === 'verifyKeyPair') {
        const { public_key, private_key, private_pass } = data
        const verify_result = mcrypt._verifyKeyPair(public_key, private_key, private_pass)
        const result = { action: 'verifyKeyPair', response_data: { verify_result } }
        process.send(result)
      }else if(action === 'end') {
        process.exit()
      }
    })

    return false
  }
}

mcrypt.pprefix = function() {
  const pprefix = typeof global === 'object' ? global.MOSY_PPREFIX : typeof window === 'object' ? window.MOSY_PPREFIX : null
  return String(pprefix??'')
} // pprefix

mcrypt.md5Windows = function(filePath, done) {
  if (os.platform() !== 'win32') {
    return done()
  }

  try {
    // Execute certutil command
    const result = child.execSync(`certutil -hashfile "${filePath}" MD5`, { encoding: 'utf8' })

    // Parse the result
    const lines = result.split('\n')
    if (lines.length >= 2) {
      const md5Hash = lines[1].trim().replace(/\s/g, '')
      done(md5Hash)
    } else {
      console.warn('No MD5 hash')
      done()
    }
  } catch (error) {
    console.error('Failed to calculate MD5 hash:' + error)
    return done()
  }
}

/**
 * calculate MD5 hash of the string
 * @param {string} password - password to be hashed
 */
mcrypt.md5 = function(text, encoding='raw') {
  let md5
  if(crypto?.createHash) {
    md5 = crypto.createHash('md5')
    md5.update(text)
    return md5.digest('hex')
  }else{
    md5 = forge.md.md5.create()
    md5.update(text, encoding)
    return md5.digest().toHex()
  }
} // md5

/**
 * @description
 * calculate MD5 hash for given nodejs stream
 * !!! important: stream must be opened as binary !!!
 * mcrypt.md5stream(fs.createReadStream(path, {encoding: 'binary'}), (md5)=>{...})
 * @param {string} stream - nodejs stream
 * @param {function} done - callback function
 */
mcrypt.md5stream = function(stream, done) {
  let done_catched = function(md5) {
    done_catched = ()=>{}
    try { done(md5) } catch(e) { console.error(e) }
  }
  if(!stream) return done_catched()
  let md5
  if(crypto?.createHash) {
    md5 = crypto.createHash('md5')
    stream
      .on('error', ()=>{ done_catched() })
      .on('data', (chunk)=>md5.update(chunk, 'binary'))
      .on('end', ()=>{ done_catched(md5.digest('hex')) })
  }else{
    md5 = forge.md.md5.create()
    stream
      .on('error', ()=>{ done_catched() })
      .on('data', (chunk)=>md5.update(chunk))
      .on('end', ()=>{ done_catched(md5.digest().toHex()) })
  }
} // md5stream

/**
 * @description
 * calculate MD5 hash for given file
 * @param {string} path - path to md5 file
 * @param {function} done - callback function
 */
mcrypt.md5file = function(path, done) {
  mcrypt.md5stream(fs.createReadStream(path, { encoding: 'binary' }), done)
} // md5file

/**
 * @description
 * hash given password; returns combined salt+hash
 * @param {string} password - password to be hashed
 */
mcrypt.generateHash = function(password) {
  password = password==null ? '' : String(password)
  const salt = mcrypt.challenge().substr(0, 16)
  const md = forge.md.sha256.create()
  md.update(salt+password, 'utf8')
  const hash = forge.util.encode64(md.digest().getBytes())
  return salt + hash
} // generateHash

/**
 * @description
 * validate given password
 * @param {string} password - password to be validated
 * @param {string} salthash - salt phrase
 * @returns boolean value whether hash has been validated correct
 */
mcrypt.validateHash = function(password, salthash) {
  password = String(password??'')
  salthash = String(salthash??'')
  if(password==='' || salthash==='') return false
  const salt = salthash.substr(0, 16)
  const hash = salthash.substr(16)
  const md = forge.md.sha256.create()
  md.update(salt+password, 'utf8')
  const hash2 = forge.util.encode64(md.digest().getBytes())
  return hash===hash2
} // validateHash

/**
 * @description
 * generates random challenge string
 * @returns string
 */
mcrypt.challenge = function() {
  return forge.util.encode64(forge.random.getBytesSync(16)).replace(/==$/, '')
} // challenge

/**
 * @description
 * generates random 32bit integer
 * @returns integer
 */
mcrypt.rand32 = function() {
  const r=forge.random.getBytesSync(4)
  return(
    r.charCodeAt(0)*16777216 +
    r.charCodeAt(1)*65536 +
    r.charCodeAt(2)*256 +
    r.charCodeAt(3)
  )
}

/**
 * @description
 * generates xor string
 * @param {string} phrase - phrase to be hashed
 */
mcrypt.xorCrypt = function(phrase, key = 6) {
  phrase = phrase==null ? '' : String(phrase)
  const phrase_length = phrase.length

  let output = ''

  for (let i = 0; i < phrase_length; ++i) {
    output += String.fromCharCode(key ^ phrase.charCodeAt(i))
  }

  return output
} // xorCrypt

/**
 * @description
 * generates mini hash of given phrase without salt; not much safe, but very small
 * @param {string} phrase - phrase to be hashed
 * @returns hash
 */
mcrypt.hash32 = function(phrase) {
  const seed = 0xABBA
  phrase = phrase==null ? '' : String(phrase)
  let hash = xxhash.h32(phrase, seed)
  hash = mcrypt.radix(hash, mcrypt.RADIX64)
  while(hash.length<6) hash = hash+'='
  return hash
} // hash32

/**
 * @description
 * generates mini hash of given phrase without salt; not much safe, but very small
 * @param {string} phrase - phrase to be hashed
 * @returns hash
 */
mcrypt.hash10 = function(phrase) {
  const seed = 0x1357
  phrase = phrase==null ? '' : String(phrase)
  let hash = xxhash.h64(phrase, seed)
  hash = mcrypt.radix(hash, mcrypt.RADIX64)
  hash = hash.substr(0, 10)
  return hash
} // hash64

/**
 * @description
 * generates the hash of the vehicle plate
 * @param {string} plate - vehicle plate
 * @param {string} country
 */
mcrypt.cryptPlate = function(plate, country) {
  let str = String(country)+String(plate)
  str = str.split('').reverse().join('')
  const str_parts = str.toUpperCase().match(/[A-Z0-9]{1,10}/g)
  let out = ''
  str_parts.forEach((part, idx)=>{
    const val = parseInt('1'+part, 36)
    if(isNaN(val)) return
    let rad = mcrypt.radix(val, mcrypt.RADIX62)
    if(idx<str_parts.length-1 && rad.length<9) {
      rad = rad.padStart(9, '0')
    }
    out += rad
  })
  return out
}

/**
 * @description
 * unhash the vehicle plate
 * @param {string} str - hashed string
 * @returns uncrypted plate
 */
mcrypt.uncryptPlate = function(str) {
  str = String(str)
  const str_parts = str.match(/[A-Za-z0-9]{1,9}/g)
  let out = ''
  str_parts.forEach(part=>{
    part = mcrypt.unradix(part, mcrypt.RADIX62).toString(36).toUpperCase()
    part = part.replace(/^1/, '')
    out += part
  })
  out = out.split('').reverse().join('')
  const m = out.match(/^([A-Z]{2})(.*)$/)
  if(!m) return
  return [m[2], m[1]]
}

/**
 * @description
 * generates mini hash of given phrase with salt; not much safe, but smaller than MD5
 * @param {string} phrase - phrase to be hashed
 * @returns salt + hash
 */
mcrypt.generateHash32_32 = function(phrase) {
  const seed = 0xABBA
  phrase = phrase==null ? '' : String(phrase)
  let salt = mcrypt.rand32()
  salt = mcrypt.radix(salt, mcrypt.RADIX64)
  while(salt.length<6) salt = salt+'='
  let hash = xxhash.h32(salt+phrase, seed)
  hash = mcrypt.radix(hash, mcrypt.RADIX64)
  while(hash.length<6) hash = hash+'='
  return salt + hash
} // generateHash32_32

/**
 * @description
 * validate given mini hash
 * @param {string} phrase - phrase to be validated
 * @param {string} salthash - salt string
 * @returns boolean value - according the hash validation
 */
mcrypt.validateHash32_32 = function(phrase, salthash) {
  const seed = 0xABBA
  phrase = String(phrase??'')
  salthash = String(salthash??'')
  if(phrase==='' || salthash==='') return false
  const salt = salthash.substr(0, 6)
  const hash = salthash.substr(6)

  let hash2 = xxhash.h32(salt+phrase, seed)
  hash2 = mcrypt.radix(hash2, mcrypt.RADIX64)
  while(hash2.length<6) hash2 = hash2+'='

  return hash===hash2
} // validateHash32_32

/**
 * @description
 * Converts integer to other radix string
 * @param {integer} [number]
 * @param {array} [digits]
 * @returns result - radix string
 */
mcrypt.radix = function(number, digits) {
  number = Math.abs(parseInt(number))
  if(isNaN(number)) return ''
  const radix = digits.length

  let result = ''
  while(1) {
    result = digits.charAt(number % radix) + result
    number = Math.floor(number / radix)
    if(number === 0) return result
  }
} // radix

/**
 * @description
 * Converts other radix string to integer
 * Converts integer to other radix string
 * @param {string} [value]
 * @param {array} [digits]
 * @returns result - integer
 */
mcrypt.unradix = function(value, digits) {
  const chars = value==null ? [] : String(value).split('')
  let result = 0
  while(chars.length>0) {
    result *= digits.length
    const chr = chars.shift()
    const val = digits.indexOf(chr)
    if(val<0) return NaN
    result += val
  }
  return result
} // unradix

/**
 * @description
 * High precision time stamp - number of microseconds since Unix Epoch
 *
 */
mcrypt._start_now = Date.now()*1000
mcrypt._start_hrtime = typeof process==='object' && process.hrtime ? process.hrtime() : null
mcrypt.now_micro = function() {
  if(typeof process==='object' && process.hrtime) {
    const diff = process.hrtime(mcrypt._start_hrtime)
    return mcrypt._start_now + diff[0]*1000000 + parseInt(diff[1]/1000)
  // }else if(typeof window=='object' && window.performance){
  //   return parseInt(1000*(performance.timing.navigationStart+performance.now()))
  }else{
    const dt = Date.now()
    if(dt===mcrypt._last_dt) {
      // same miliseconds as previous request
      mcrypt._last_micros++
      if(mcrypt._last_micros>=1000) {
        // THIS IS REALLY BAD !!! system asked for more than 1000 microseconds in one milisecond
        console.error('TOO MUCH MICROSECONDS: '+dt)
      }
    }else{
      // new miliseconds, restart "microseconds" counter
      mcrypt._last_dt = dt
      mcrypt._last_micros = 0
    }
    return dt*1000+mcrypt._last_micros
  }
} // now

const MOSY_EPOCH_SECONDS = 1483228800
mcrypt._encodeSeconds5 = function(seconds_since_epoch) {
  let seconds = parseInt(seconds_since_epoch) - MOSY_EPOCH_SECONDS
  if(!(seconds>0)) seconds=0
  seconds = seconds % Math.pow(62, 5) // ensure maximum of 5 "digits"
  seconds = mcrypt.radix(seconds, mcrypt.RADIX62) // convert to 62radix string
  seconds = String('00000' + seconds).slice(-5) // padding, if necessary, to always return 5 digits
  return seconds
}

/**
 * @description
 * Generates unique oid
 */
mcrypt.oid = function() {
  const now = mcrypt.now_micro()

  // encode seconds to 5 "digits"
  const seconds = mcrypt._encodeSeconds5(parseInt(now/1000000))

  // encode microseconds to 4 "digits"
  let micro = now % 1000000
  micro = mcrypt.radix(micro, mcrypt.RADIX62) // convert to 62radix string
  micro = String('0000' + micro).slice(-4) // padding, if necessary, to always return 5 digits

  // initial "0" indicates this is basic timestamp OID
  return '0'+seconds+micro
}

mcrypt.oid_time_with_counter = function(domain, counter) {
  domain = String(domain??'')
  if(domain.length!==1 || domain==='0' || mcrypt.RADIX62.indexOf(domain)<0) return

  // encode seconds to 5 "digits"
  const seconds = mcrypt._encodeSeconds5(parseInt(Date.now()/1000))

  // encode counter value to 4 "digits" (range 0-14.776.336)
  counter = parseInt(counter) || 0
  if(counter<0) counter=0
  counter = counter % Math.pow(62, 4) // ensure maximum of 4 "digits"
  counter = mcrypt.radix(counter, mcrypt.RADIX62) // convert to 62radix string
  counter = String('0000' + counter).slice(-4) // padding, if necessary, to always return 5 digits

  // initial "0" indicates this is basic timestamp OID
  return '0'+seconds+counter
}

/**
 * @description
 * Generates unique application ID with given prefix (e.g. P for mPatrol, B for mBox, x for temporary...)
 * Uses digits and upper and lower case letters (62 different characters).
 * @param {string} [prefix] - prefix put before generated UUID
 */
mcrypt.generate_app_id = function(prefix) {
  prefix = prefix==null ? '' : String(prefix)
  const seconds = mcrypt._encodeSeconds5(parseInt(Date.now()/1000))
  return prefix+seconds
} // generate_app_id

/**
 * @description
 * Generates Key pair using OpenSSL backend
 * @param key_password
 * @param callback
 * @private
 */
mcrypt._generateKeyPairOpenSSL = function(key_password, callback) {
  child.exec(OPENSSL_COMMAND_GENERATE_KEY, (err, stdout) => {
    if(err) {
      return callback()
    }
    const private_pem = stdout.toString()
    const generate_public = child.exec(OPENSSL_COMMAND_EXPORT_PUBLIC_KEY, (err, stdout) => {
      if(err) {
        return callback()
      }
      const public_pem = stdout.toString()
      callback([public_pem, private_pem])
    })
    generate_public.stdin.write(private_pem)
  })
} // generateKeyPairOpenSSL

/**
 * @description
 * Generates Key pair using Forge backend
 * @param key_password
 * @param callback
 * @private
 */
mcrypt._generateKeyPairForge = function(key_password, callback) {
  forge.rsa.generateKeyPair({ bits: KEY_SIZE }, (err, keypair) => {
    if(err || !keypair) return callback()
    const public_pem = mcrypt.publicToPEM(keypair.publicKey)
    const private_pem = mcrypt.privateToPEM(keypair.privateKey, key_password)
    callback([public_pem, private_pem])
  })
} // generateKeyPairForge

/**
 * @description
 * Function which decides if OpenSSL or Forge variant should be executed. Function performs one time check if OpenSSL is available
 * @param key_password
 * @param callback
 * @private
 */
mcrypt._generateKeyPair = function(key_password, callback) {
  // Do OpenSSL Detection
  if(mcrypt._generateKeyPair.isOpenSSL === undefined) {
    if(!mcrypt) {
      mcrypt._generateKeyPair.isOpenSSL = false
      mcrypt._generateKeyPair(key_password, callback)
      return
    }

    child.exec('/usr/bin/openssl genrsa 2048', (err) => {
      if(err) {
        mcrypt._generateKeyPair.isOpenSSL = false
      }else {
        mcrypt._generateKeyPair.isOpenSSL = true
      }
      mcrypt._generateKeyPair(key_password, callback)
    })

    return
  }

  // We have to check if OpenSSL is available
  if(child.exec && mcrypt._generateKeyPair.isOpenSSL) {
    mcrypt._generateKeyPairOpenSSL(key_password, callback)
  } else {
    mcrypt._generateKeyPairForge(key_password, callback)
  }
} // _generateKeyPair

/**
 * generates private/public key pair as PEM
 * @param {string} key_password - password to be used for encryption of private key
 */
mcrypt.generateKeyPair = function(key_password, callback) {
  if(typeof callback==='function') {
    if(cryptoWorker) {
      // CryptoWorker is available we will use it
      requests_generateKeyPair.push({ key_password, callback })
      cryptoWorker.send({ action: 'generateKeyPair', data: { key_password } })
    }else{
      // No CryptoWorker we will fallback to default callback implementation
      mcrypt._generateKeyPair(key_password, callback)
    }
  }else{
    const keypair = forge.rsa.generateKeyPair({ bits: KEY_SIZE })
    const public_pem = mcrypt.publicToPEM(keypair.publicKey)
    const private_pem = mcrypt.privateToPEM(keypair.privateKey, key_password)
    return([public_pem, private_pem])
  }
} // generateKeyPair

/**
 * @description
 * verifies that given public and private key match
 * @param {string} public_key
 * @param {string} private_key
 * @param {string} private_pass
 */
mcrypt._verifyKeyPair = function(public_key, private_key, private_pass) {
  private_pass = private_pass == null ? '' : String(private_pass)
  try{
    let verified = false
    const test_phrase = mcrypt.challenge()

    if(crypto?.createSign && crypto?.createVerify) {
      if(typeof (public_key) !== 'string') public_key = mcrypt.publicToPEM(public_key)
      if(typeof (private_key) !== 'string') private_key = mcrypt.privateToPEM(private_key, private_pass)

      const signer = crypto.createSign('sha256').update(test_phrase)
      const passphrase = mcrypt.pprefix() + private_pass
      const sign = signer.sign({ key: private_key, passphrase }, 'base64')
      const verifier = crypto.createVerify('sha256').update(test_phrase)

      verified = verifier.verify(public_key, sign, 'base64')
    }else{
      if(typeof (public_key) === 'string') public_key = mcrypt.publicFromPEM(public_key)
      if(typeof (private_key) === 'string') private_key = mcrypt.privateFromPEM(private_key, private_pass)

      const md = forge.md.sha256.create().update(test_phrase, 'utf8')
      const signature = private_key.sign(md)

      verified = public_key.verify(md.digest().bytes(), signature)
    }

    return verified
  } catch (e) {
    return false
  }
} // _verifyKeyPair

/**
 * @description
 * verifies that given public and private key match. It can be called in synchronous way or by callback.
 * It will use cryptoworker in case it is available (Only when using callback).
 */
mcrypt.verifyKeyPair = function(public_key, private_key, private_pass, callback) {
  if(typeof callback === 'function') {
    if(cryptoWorker) {
      // CryptoWorker is available we will use it
      requests_verifyKeyPair.push({ public_key, private_key, private_pass, callback })
      cryptoWorker.send({ action: 'verifyKeyPair', data: { public_key, private_key, private_pass } })
      return
    }
    const verify_result = mcrypt._verifyKeyPair(public_key, private_key, private_pass)

    callback(null, verify_result)
  }
  return mcrypt._verifyKeyPair(public_key, private_key, private_pass)
} // verifyKeyPair

/**
 * @description
 * verifies that certificate's public key and given private key match
 * returns app_id from certificate or empty string
 * @param {string} cert_pem
 * @param {string} private_pem
 * @param {string} private_pass
 */
mcrypt.verifyMyCertificate = function(cert_pem, private_pem, private_pass, callback) {
  if(!cert_pem || !private_pem) return ''
  try{
    // verify certificate itself for exppiration
    const chain = mcrypt.certsFromPEM(cert_pem)
    if(chain.length===0) return ''
    const cert = chain[0]; const ca = chain[1]
    const app_id = cert.subject.getField('CN').value.replace(/\..*/, '')
    const public_pem = mcrypt.publicToPEM(cert.publicKey)
    let verified = mcrypt.verifyKeyPair(public_pem, private_pem, private_pass)
    if(!verified) return ''
    if(typeof (MOSY_APP)!=='undefined' && MOSY_APP==='mServer' && chain.length===1) return app_id
    const caStore = forge.pki.createCaStore([ca]) // pin our CA
    verified = forge.pki.verifyCertificateChain(caStore, [cert])
    if(!verified) return ''
    if(typeof callback === 'function') callback(app_id)
    return app_id
  }catch(e) {
    console.error('verifyMyCertificate error: '+e)
    if(typeof callback === 'function') callback('')
    return ''
  }
} // verifyMyCertificate

/**
 * @description
 * splits several PEM certificates in one string into list of single certificates
 * @param {string} combined_pem
 * @returns array of strings
 */
mcrypt.splitPem = function(combined_pem) {
  let m; const pems = []
  const re = /^--+\s*BEGIN\s+CERTIFICATE\s*--+\r?\n[\s\S]*?\r?\n--+\s*END\s+CERTIFICATE\s*--+\r?\n/mg
  while((m = re.exec(combined_pem))!=null) {
    pems.push(m[0])
  }
  return pems
} // splitPem

/**
 * @description
 * decrypts private key from PEM using provided password to node-forge private key object
 * @param {string} private_pem
 * @param {string} password
 * @returns array of strings
 */
mcrypt.privateFromPEM = function(private_pem, password) {
  if(!private_pem) return null
  try{
    password = String(password??'')
    if(password === '') {
      password = null
    }else {
      password = mcrypt.pprefix() + password
    }
    const key = forge.pki.decryptRsaPrivateKey(private_pem, password)
    return key || null
  }catch(e) {
    console.error('privateFromPEM: '+e)
    return null
  }
} // privateFromPEM

/**
 * @description
 * encrypts private key from node-forge private key object to PEM using provided password
 * @param {string} key - key to be encrypted
 * @param {string} password - crypting password
 * @returns array of strings
 */
mcrypt.privateToPEM = function(key, password) {
  if(!key) return null
  try {
    password = String(password??'')
    if(password === '') {
      password = null
    }else {
      password = mcrypt.pprefix() + password
    }
    const pem = password === null ? forge.pki.privateKeyToPem(key) : forge.pki.encryptRsaPrivateKey(key, password)
    return pem || null
  } catch (e) {
    console.error('privateToPEM: ' + e)
    return null
  }
} // privateToPEM

/**
 * @description
 * decrypts private key from PEM using provided password end returns again in PEM
 * @param {string} private_pem
 * @param {string} password
 * @returns PEM string or null if unsuccessful
 */
mcrypt.decryptPEM = function(private_pem, password) {
  let key = mcrypt.privateFromPEM(private_pem, password)
  if(!key) return null
  try{
    key = forge.pki.privateKeyToPem(key)
    return key || null
  }catch(e) {
    console.error('decryptPEM: '+e)
    return null
  }
} // decryptPEM

/**
 * @description
 * transforms public key from PEM into node-forge public key object
 * @param {string} public_pem
 * @returns object or null on error
 */
mcrypt.publicFromPEM = function(public_pem) {
  if(!public_pem) return null
  try{
    const key = forge.pki.publicKeyFromPem(public_pem)
    return key || null
  }catch(e) {
    console.error('publicFromPEM: '+e)
    return null
  }
} // publicFromPEM

/**
 * @description
 * transforms public key from node-forge public key object to PEM
 * @param {string} key
 * @returns object or null on error
 */
mcrypt.publicToPEM = function(key) {
  if(!key) return null
  try{
    const pem = forge.pki.publicKeyToPem(key)
    return pem || null
  }catch(e) {
    console.error('publicToPEM: '+e)
    return null
  }
} // publicToPEM

/**
 * @description
 * returns public key from certificate
 * @param {string|object} cert - node-forge's certificate object or PEM-encoded certificate
 * @returns node-forge public key object or null on error
 */
mcrypt.publicFromCert = function(cert) {
  if(!cert) return null
  try{
    if(typeof (cert)==='string') {
      const chain = mcrypt.certsFromPEM(cert)
      cert = chain[0]
    }
    return cert.publicKey
  }catch(e) {
    return null
  }
} // publicFromCert

/**
 * @description
 * transforms PEM certificate(s) into list of node-forge certificate objects
 * returns null if any of them failed to parse or there is no certificate
 * @param {string} certs_pem - one or more certificates in PEM format in one string
 * @returns array of node-forge certificates or null
 */
mcrypt.certsFromPEM = function(certs_pem) {
  certs_pem = mcrypt.splitPem(certs_pem)
  if(certs_pem.length===0) return null
  const certs = []
  for(let i=0; i<certs_pem.length; i++) {
    try{
      const cert = forge.pki.certificateFromPem(certs_pem[i])
      if(!cert) return null
      certs.push(cert)
    }catch(e) {
      return null
    }
  }
  return certs
} // certsFromPEM

/**
 * @description
 * create signature of challenge
 * @param {string} private_key
 * @param {object} data
 * @returns base64 encoded signature
 */
mcrypt.sign = function(private_key, data) {
  if(crypto?.createSign) {
    return mcrypt.signCrypto(private_key, data)
  }else {
    return mcrypt.signForge(private_key, data)
  }
} // sign

/**
 * @description
 * create signature of challenge using Forge crypto library
 * @param {string} private_key
 * @param {object} data
 * @returns base64 encoded signature
 */
mcrypt.signForge = function(private_key, data) {
  try{
    if(typeof (private_key)==='string') private_key = forge.pki.privateKeyFromPem(private_key)
    const md = forge.md.sha256.create()
    if(typeof (data)==='string') {
      md.update(data, 'utf8')
    }else if(typeof Buffer !== 'undefined') {
      if(!(data instanceof Buffer)) data = Buffer.from(data)
      const buf = data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) // this is important; for small lengths, internal Buffer.buffer may share the space with other variables and provide incorrect length
      const forge_buf = new forge.util.ByteBuffer(buf)
      md.update(forge_buf.getBytes())
    }
    let signature = private_key.sign(md)
    signature = forge.util.encode64(signature)
    return signature
  }catch(e) {
    return null
  }
} // signForge

/**
 * @description
 * create signature of challenge using native NodeJS crypto library
 * @returns base64 encoded signature
 */
mcrypt.signCrypto = function(private_key, data) {
  try{
    const private_key_pem = typeof (private_key)==='string' ? private_key : forge.pki.privateKeyToPem(private_key)
    const md = crypto.createSign('RSA-SHA256')
    md.update(data, 'binary')
    return md.sign(private_key_pem, 'base64')
  }catch(e) {
    return null
  }
} // signCrypto

/**
 * @description
 * verify signature of challenge
 * @returns true/false
 */
mcrypt.verifySignature = function(pub_key, data, signature_base64) {
  if(crypto?.createVerify) {
    return mcrypt.verifySignatureCrypto(pub_key, data, signature_base64)
  }else {
    return mcrypt.verifySignatureForge(pub_key, data, signature_base64)
  }
} // verifySignature

/**
 * @description
 * verify signature of challenge using Forge library
 * @returns true/false
 */
mcrypt.verifySignatureForge = function(pub_key, data, signature_base64) {
  if(typeof (pub_key)==='string') { pub_key = mcrypt.publicFromPEM(pub_key) }

  try{
    const signature = forge.util.decode64(signature_base64)
    const md = forge.md.sha256.create()

    if(typeof (data)==='string') {
      md.update(data, 'utf8')
    }else if(typeof Buffer !== 'undefined') {
      if(!(data instanceof Buffer)) data = Buffer.from(data)
      const buf = data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) // this is important; for small lengths, internal Buffer.buffer may share the space with other variables and provide incorrect length
      const forge_buf = new forge.util.ByteBuffer(buf)
      md.update(forge_buf.getBytes())
    }

    const verified = pub_key.verify(md.digest().bytes(), signature)
    return(!!verified)
  }catch(e) {
    return false
  }
} // verifySignatureForge

/**
 * @description
 * verify signature of challenge using NodeJS Crypto library
 * @returns true/false
 */
mcrypt.verifySignatureCrypto = function(pub_key, data, signature_base64) {
  try{
    if(typeof (pub_key)==='object') { pub_key = forge.pki.publicKeyToPem(pub_key) }
    const md = crypto.createVerify('RSA-SHA256')
    md.update(data, 'binary')
    return md.verify(pub_key, signature_base64, 'base64')
  }catch(e) {
    return null
  }
} // verifySignatureCrypto

/**
 * @description
 * verify other certificate using mine certificate's direct approver
 * we verify other side expecting we both share the same signing entity
 * @returns {app_id:APPID, pub_key: APP_PUBKEY} from the other side's certificate or null if invalid
 */
mcrypt.verifyCertificateWithMyCA = function(my_cert_pem, other_cert_pem, ignore_ca_check) {
  try{
    const my_chain = mcrypt.certsFromPEM(my_cert_pem)
    const my_issuer = my_chain[1]
    const caStore = forge.pki.createCaStore([my_issuer]) // pin our CA
    const other_chain = mcrypt.certsFromPEM(other_cert_pem)
    const other_cert = other_chain[0]
    let verified = false
    try{
      verified = forge.pki.verifyCertificateChain(caStore, [other_cert])
    }catch(e) {
      // ignore
    }
    if(!verified && !ignore_ca_check) return null
    const app_id = other_cert.subject.getField('CN').value.replace(/\..*/, '')
    let altName
    try{
      altName = other_cert.getExtension('subjectAltName').altNames.filter(function(alt) {
        return String(alt.type??'')==='0'
      })[0].value
    }catch(e) {
      altName = null
    }
    const m = /^(\w+)\.(\w+)$/.exec(altName)
    if(m && m[1]!==app_id) return null
    const apptype =
      m ? m[2] :
        /^B/.test(app_id) ? 'mBox' :
          /^P/.test(app_id) ? 'mPatrol' :
            /^S/.test(app_id) ? 'mServer' :
              /^O/.test(app_id) ? 'mOffice' :
                /^Y/.test(app_id) ? 'mPlayer' :
                  /^R/.test(app_id) ? 'mRadar' :
                    /^D/.test(app_id) ? 'mDoppler' :
                      /^E/.test(app_id) ? 'mExternal' :
                        /^T/.test(app_id) ? 'mTerm' :
                          /^I/.test(app_id) ? 'mIntegration' :
                            null
    if(!apptype) return null

    const pub_key = other_cert.publicKey
    if(!app_id || !pub_key) return null
    return({ app_id, apptype, pub_key })
  }catch(e) {
    return null
  }
} // verifyCertificateWithMyCA

/**
 * @description
 * create client certificate freshly signed by CA certificate
 * @returns array of node-forge certificates or null
 */
mcrypt._createCertificate = function(ca_cert_pem, ca_key_pem, ca_key_pass, cert_valid_seconds, app_id, apptype, app_pub_pem) {
  const ca_chain = mcrypt.certsFromPEM(ca_cert_pem)

  const ca_key = mcrypt.privateFromPEM(ca_key_pem, ca_key_pass)

  const app_pub = mcrypt.publicFromPEM(app_pub_pem)

  cert_valid_seconds = parseInt(cert_valid_seconds)
  if(!ca_chain || ca_chain.length<1 || !ca_key || !app_pub || !cert_valid_seconds || cert_valid_seconds<0) return

  const ca_cert = ca_chain[0]
  const ca_cert_attributes = ca_cert.subject.attributes

  const app_cert = forge.pki.createCertificate()

  app_cert.publicKey = app_pub
  app_cert.serialNumber = '001'
  const now = Date.now() - 24*60*60*1000 // start 1 day earlier to prevent a bit different time between client and server
  app_cert.validity.notBefore = new Date(now)
  app_cert.validity.notAfter = new Date(now + 1000*cert_valid_seconds)
  app_cert.setSubject([
    {
      shortName: 'CN',
      value: app_id+'.'+ca_cert.subject.getField('CN').value.replace(/^\*\./, ''),
    },
  ])
  app_cert.setExtensions([{
    name: 'subjectAltName',
    altNames: [{
      type: 0,
      value: app_id+'.'+apptype,
    }],
  }])

  app_cert.setIssuer(ca_cert_attributes)

  app_cert.setExtensions([{
    name: 'keyUsage',
    digitalSignature: true,
    keyEncipherment: true,
  }])

  //console.timeLog('_createCertificate', "settingCertificate");

  const sha256 = forge.md.sha256.create()
  //console.timeLog('_createCertificate', "sha256.create");

  app_cert.sign(ca_key, sha256)
  //console.timeLog('_createCertificate', "sign");

  const ret = forge.pki.certificateToPem(app_cert)+'\n'+forge.pki.certificateToPem(ca_cert)
  //console.timeLog('_createCertificate', "conversion");

  // console.timeEnd('_createCertificate');
  return ret
}

/**
 * create client certificate freshly signed by CA certificate
 * @returns array of node-forge certificates or null
 */
mcrypt.createCertificate = function(ca_cert_pem, ca_key_pem, ca_key_pass, cert_valid_seconds, app_id, apptype, app_pub_pem, callback) {
  // We have Callback defined so we will use it
  if(typeof callback==='function') {
    if(cryptoWorker) {
      // CryptoWorker is available we will use it
      requests_createCertificate.push({ ca_cert_pem, ca_key_pem, ca_key_pass, cert_valid_seconds, app_id, apptype, app_pub_pem, callback })
      cryptoWorker.send({ action: 'createCertificate', data: { ca_cert_pem, ca_key_pem, ca_key_pass, cert_valid_seconds, app_id, apptype, app_pub_pem, callback } })
      return
    }

    // If we do not have a callback we will keep current solutions
    const certificate = mcrypt._createCertificate(ca_cert_pem, ca_key_pem, ca_key_pass, cert_valid_seconds, app_id, apptype, app_pub_pem)

    callback(null, certificate)
  }else{
    callback(new Error('Missing callback function'))
  }
} // createCertificate

/**
 * create client certificate freshly signed by CA certificate in Synchronous way
 * @returns array of node-forge certificates or null
 */
mcrypt.createCertificateSync = function(ca_cert_pem, ca_key_pem, ca_key_pass, cert_valid_seconds, app_id, apptype, app_pub_pem) {
  const certificate = mcrypt._createCertificate(ca_cert_pem, ca_key_pem, ca_key_pass, cert_valid_seconds, app_id, apptype, app_pub_pem)

  return certificate
} // createCertificateSync
